// src/components/PublicHousePoints/HousePoints.tsx
import React, { useMemo, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { experimental_sx as sx } from '@mui/system';
import PageTitle from 'components/PageTitle';
import { School } from 'models/school';
import { SchoolHouse } from 'models/house';
import { HouseTotalsRow } from 'services/awards';

type Props = {
  school: School;
  houses: SchoolHouse[];
  totals: HouseTotalsRow[];
  title?: string;
  showTitle?: boolean;
};

/* ---------- Typography styled like Activities (grey, bold) ---------- */

const HPRank = styled(Typography)(
  sx({
    display: 'block',
    fontWeight: 'bold',
    color: 'text.secondary',
    textAlign: 'center',
    width: 32,
    lineHeight: 1.2
  })
);

const HPName = styled(Typography)(
  sx({
    display: 'block',
    fontWeight: 'bold',
    color: 'text.secondary',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  })
);

const HPPoints = styled(Typography)(
  sx({
    display: 'block',
    fontWeight: 'bold',
    color: 'text.secondary',
    fontSize: { xs: 14, md: 16 },
    lineHeight: 1.2
  })
);

/* ---------- Motion utilities ---------- */

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window)) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = () => setReduced(mq.matches);
    handler();
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);
  return reduced;
}

function useCountUp(
  target: number,
  opts: { duration?: number; delay?: number } = {}
) {
  const { duration = 1000, delay = 0 } = opts;
  const [value, setValue] = useState(0);
  const rafRef = useRef<number | null>(null);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (reduced) {
      setValue(target);
      return;
    }

    const startFrom = value; // capture current displayed value
    let startTime: number | null = null;
    let mounted = true;

    const step = (ts: number) => {
      if (!mounted) return;
      if (startTime === null) startTime = ts + delay;
      const elapsed = ts - startTime;

      if (elapsed < 0) {
        rafRef.current = requestAnimationFrame(step);
        return;
      }

      const t = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(t);
      const next = startFrom + (target - startFrom) * eased;
      setValue(next);

      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      mounted = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration, delay, reduced]);

  return value;
}

/* ---------- Row component (uses hooks safely) ---------- */

type RowProps = {
  index: number;
  totalPoints: number;
  houseId: string;
  houseNameFallback?: string;
  maxPts: number;
  nf: Intl.NumberFormat;
  getHouseLogo: (houseId: string) => string;
  getHouseAccent: (houseId: string) => string | undefined;
  getHouseName: (houseId: string, fb?: string) => string;
};

const HousePointsRow: React.FC<RowProps> = ({
  index,
  totalPoints,
  houseId,
  houseNameFallback,
  maxPts,
  nf,
  getHouseLogo,
  getHouseAccent,
  getHouseName
}) => {
const animated = useCountUp(totalPoints, {
duration: 3000,      
delay: index * 240    
});
  const displayPoints = Math.round(animated);
  const percent = Math.max(
    0,
    Math.min(100, Math.round((animated / Math.max(maxPts, 1)) * 100))
  );

const DEFAULT_BAR_COLOR = 'grey.700';

  return (
    <Box
      component="li"
      className="house-card"
      sx={{
        display: 'grid',
        gridTemplateColumns: '32px 44px 1fr auto',
        alignItems: 'center',
        gap: 1,
        py: 1
      }}
    >
      <HPRank className="house-rank">{index + 1}</HPRank>

      <Box
        className="house-logo"
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <img
          src={getHouseLogo(houseId)}
          alt={getHouseName(houseId, houseNameFallback)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </Box>

      <Box className="house-meta" sx={{ minWidth: 0 }}>
        <HPName
          className="house-name"
          title={getHouseName(houseId, houseNameFallback)}
        >
          {getHouseName(houseId, houseNameFallback)}
        </HPName>

        <Box
        className="house-bar"
        sx={{ mt: 0.5, height: 8, bgcolor: 'action.hover', borderRadius: 5, overflow: 'hidden' }}
        >
        <Box
            className="house-bar-fill"
            sx={{
            height: '100%',
            width: `${percent}%`,
            bgcolor: getHouseAccent(houseId) || DEFAULT_BAR_COLOR, // 👈 fallback to dark grey
            transition: 'width 300ms ease'
            }}
            aria-label={`${getHouseName(houseId, houseNameFallback)} progress`}
        />
        </Box>
      </Box>

      <HPPoints className="house-points" sx={{ ml: 1 }}>
        {nf.format(displayPoints)}&nbsp;pts
      </HPPoints>
    </Box>
  );
};

/* ---------- Main component ---------- */

const HousePoints: React.FC<Props> = ({
  school,
  houses,
  totals,
  title = 'House Points',
  showTitle = true
}) => {
  const housesById = useMemo(() => {
    return (houses || []).reduce<Record<string, SchoolHouse>>((m, h: any) => {
      m[String(h?.id ?? h?._id)] = h;
      return m;
    }, {});
  }, [houses]);

  const getHouseLogo = (houseId: string) => {
    const h = housesById[houseId] as any;
    const fallback = school?.pic || '/dist/images/empty_pic_uploader_box.png';
    return h?.pic || h?.picture || fallback;
  };

  const getHouseAccent = (houseId: string) => {
    const h = housesById[houseId] as any;
    const c = h?.colors?.[0];
    return c && c.trim() ? c : undefined;
  };

  const getHouseName = (houseId: string, fallback?: string) =>
    (housesById[houseId] as any)?.name || fallback || '';

  const nf = useMemo(() => new Intl.NumberFormat(), []);
  const maxPts = useMemo(
    () => Math.max(...(totals || []).map(h => h.totalPoints), 1),
    [totals]
  );

  const sorted = useMemo(
    () => (totals || []).slice().sort((a, b) => b.totalPoints - a.totalPoints),
    [totals]
  );

  return (
    <Box className="house-points-widget">
      {showTitle && <PageTitle text={title} />}

      <Box sx={{ p: 2, borderRadius: 2, boxShadow: 1, bgcolor: 'background.paper' }}>
        {!sorted.length ? (
          <Box className="empty-state" sx={{ fontSize: 14, color: 'text.secondary' }}>
            No points recorded yet.
          </Box>
        ) : (
          <Box component="ul" className="house-points-list" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {sorted.map((h, idx) => (
              <HousePointsRow
                key={h.houseId}
                index={idx}
                totalPoints={h.totalPoints}
                houseId={h.houseId}
                houseNameFallback={h.houseName}
                maxPts={maxPts}
                nf={nf}
                getHouseLogo={getHouseLogo}
                getHouseAccent={getHouseAccent}
                getHouseName={getHouseName}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default HousePoints;
